import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";
import type { Tone } from "@/types/tone";
import { Icon, type IconName } from "../icon";
import { IconButton } from "../icon-button";
import { Text } from "../typography/text";
import { Title } from "../typography/title";

type BannerTone = Extract<
  Tone,
  "neutral" | "positive" | "warning" | "negative" | "informative"
>;

const bannerVariants = cva(
  [
    "flex",
    "w-full",
    "items-center",
    "justify-between",
    "px-(--spacing-24)",
    "py-(--spacing-16)",
    "rounded-(--radius-sm)",
    "relative",
    "after:absolute",
    "after:bottom-0",
    "after:left-0",
    "after:right-0",
    "after:h-px",
    "after:bg-outline-subtle",
  ],
  {
    variants: {
      tone: {
        neutral: ["bg-surface-container-low", "text-surface-on-surface"],
        positive: ["bg-positive-container", "text-positive-on-container"],
        warning: [
          "bg-accessibility-warning-container",
          "text-warning-on-container",
        ],
        negative: ["bg-negative-container", "text-negative-on-container"],
        informative: [
          "bg-informative-container",
          "text-informative-on-container",
        ],
      },
    },
    defaultVariants: {
      tone: "neutral",
    },
  },
);

export interface BannerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "className" | "title"> {
  /** Icon to display — consumer-provided, varies by context */
  icon: IconName;
  /** Primary banner message */
  title: React.ReactNode;
  /** Optional supporting description shown below the title */
  description?: React.ReactNode;
  /** Tone controls background color. Defaults to "neutral". */
  tone?: BannerTone;
  /** Optional action slot rendered to the left of the dismiss button. Accepts one or two buttons. */
  action?: React.ReactNode;
  /** Show a dismiss button and call this when clicked. Omit for a persistent banner. */
  onDismiss?: () => void;
}

/**
 * Full-width page-level notification with icon, message, optional action, and dismiss.
 *
 * @example
 * const [open, setOpen] = useState(true);
 * {open && (
 *   <Banner
 *     icon="circle-check"
 *     tone="positive"
 *     title="Your changes have been saved."
 *     onDismiss={() => setOpen(false)}
 *   />
 * )}
 *
 * @example
 * // Two actions — secondary ghost button on the left, primary outline on the right
 * <Banner
 *   icon="circle-info"
 *   title="Your subscription requires attention."
 *   action={
 *     <>
 *       <Button variant="ghost" size="sm">Learn more</Button>
 *       <Button variant="outline" size="sm">Review Now</Button>
 *     </>
 *   }
 *   onDismiss={() => setOpen(false)}
 * />
 */
function Banner({
  icon,
  tone = "neutral",
  title,
  description,
  action,
  onDismiss,
  ...props
}: BannerProps) {
  const isNegative = tone === "negative";

  return (
    <div
      role={isNegative ? "alert" : "status"}
      aria-live={isNegative ? "assertive" : "polite"}
      className={cn(bannerVariants({ tone }))}
      {...props}
    >
      <div className="flex flex-1 items-start gap-(--spacing-8) min-w-0">
        <Icon name={icon} size="md" tone="inherit" />
        <div className="min-w-0 flex flex-col gap-(--spacing-8)">
          <Title as="span" size="md" style={{ color: "inherit" }}>
            {title}
          </Title>
          {description ? (
            <Text as="span" size="md" style={{ color: "inherit" }}>
              {description}
            </Text>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-start gap-(--spacing-8)">
        {action}
        {onDismiss ? (
          <IconButton
            icon="x-close"
            aria-label="Dismiss banner"
            variant="ghost"
            size="sm"
            onClick={onDismiss}
          />
        ) : null}
      </div>
    </div>
  );
}

export { Banner };
